import { InfoIcon } from "@/icons";
import { useEffect, useState, useMemo } from "react";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import {
  InventoryPokemonsOutput,
  PokemonBoxData,
  CollectionData,
} from "types/web-app";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import { getBoxes } from "@/services/box/get-boxes";
import { getCollections } from "@/services/collection/get-collections";
import { InventoryList } from "./inventory-list";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import cn from "classnames";
import { COLLECTIONS } from "game/data/collections";
import { CollectionListView } from "../collection/collection-list-view";

export const InventoryPokeView = () => {
  const {
    intent,
    selectedPokemons,
    setSelectedPokemons,
    setIntent,
    setCurrentRoute,
    setSelectedTeam,
  } = useSPAContext();
  const { getAccessToken } = useAuth();

  const [activeTab, setActiveTab] = useState<"inventory" | "collections">(
    "inventory",
  );

  const [boxes, setBoxes] = useState<PokemonBoxData[]>([]);
  const [collections, setCollections] = useState<CollectionData[]>([]);

  // Merge static collections with API data
  const mergedCollections = useMemo(() => {
    return COLLECTIONS.map((staticCol) => {
      const apiCol = collections.find((c) => c.id === staticCol.id);
      return {
        ...staticCol,
        total_slots: staticCol.slots.length,
        obtained_count: apiCol?.obtained_count || 0,
      };
    });
  }, [collections]);

  const [pokemons, setPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (intent) {
      setActiveTab("inventory");
    }
  }, [intent]);

  const getPokemons = async () => {
    console.log("🔍 Fetching inventory pokemons...");
    setIsLoading(true);
    setError(null);

    try {
      const accessToken = getAccessToken();

      if (!accessToken) {
        console.error("❌ No access token available");
        setError("Authentication failed. Please try logging in again.");
        return;
      }

      const response = await listInventoryPokemons(accessToken);
      setPokemons(response.data?.player_pokemons ?? []);
    } catch (err) {
      console.error("❌ Failed to fetch pokemons:", err);
      setError("Failed to load your pokemons. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const [boxesRes, collectionsRes] = await Promise.all([
        getBoxes(token),
        getCollections(token),
      ]);

      if (boxesRes.ok) setBoxes(boxesRes.data.boxes);
      if (collectionsRes.ok) setCollections(collectionsRes.data.collections);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    void getPokemons();
    void fetchDashboardData();
  }, []);

  const handleRefresh = async () => {
    await Promise.all([getPokemons(), fetchDashboardData()]);
  };

  const handleDoneSelectingPokesForTeam = () => {
    setIntent(undefined);
    setSelectedTeam((prev) =>
      prev ? { ...prev, pokemons: selectedPokemons } : prev,
    );
    setSelectedPokemons([]);
    setCurrentRoute(RouteNames.TEAM_EDIT);
  };

  const handleDoneSelectingPokesToBreed = () => {
    if (selectedPokemons.length !== 2) {
      Alert("You must select 2 Pokémon to breed");
      return;
    }

    setIntent(undefined);
    setCurrentRoute(RouteNames.BREED);
  };

  return (
    <div className="flex flex-col gap-2 w-full h-full pb-2 pt-2 bg-black/50 backdrop-blur-[5px] overflow-hidden">
      {/* Intent Headers */}
      {intent === Intent.SELECTING_POKEMONS_FOR_TEAM && (
        <div className="bg-orange-500 w-full flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 px-4 py-2 bg-orange-500/50">
            <InfoIcon width={24} height={24} stroke="#000000" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Selecting Pokes for Team
            </p>
            <button
              className="bg-orange-800 text-pk-white px-2 xs:px-4 py-1 rounded-md z-30 ml-auto"
              onClick={handleDoneSelectingPokesForTeam}
            >
              <span className="text-xs xs:text-sm">Done</span>
            </button>
          </div>
        </div>
      )}

      {intent === Intent.SELECTING_POKEMON_FOR_MAIN_TEAM && (
        <div className="bg-pk-orange-2 w-full flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 px-4 py-2 bg-pk-orange-2/50">
            <InfoIcon width={24} height={24} stroke="#000000" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Select Pokémon for Main Team
            </p>
            <button
              className="bg-pk-orange-5 text-pk-white px-2 xs:px-4 py-1 rounded-md z-30 ml-auto shadow-cartoon-sm"
              onClick={() => setIntent(undefined)}
            >
              <span className="text-xs xs:text-sm">Cancel</span>
            </button>
          </div>
        </div>
      )}

      {intent === Intent.SELECTING_POKEMONS_TO_BREED && (
        <div className="z-20 w-full border-b-4 border-b-pk-orange-2 flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 p-2 bg-pk-orange-5">
            <InfoIcon width={24} height={24} stroke="#8c4237" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Select 2 Pokémon to Breed
            </p>
            <SimpleButton
              variant="orange"
              onClick={handleDoneSelectingPokesToBreed}
              className="ml-auto"
            >
              Done
            </SimpleButton>
          </div>
        </div>
      )}

      {intent === Intent.SELECTING_POKEMON_FOR_COLLECTION && (
        <div className="bg-pk-blue-2 w-full flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 px-4 py-2 bg-pk-blue-2/50">
            <InfoIcon width={24} height={24} stroke="#000000" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Select Pokémon for Collection (
              {window.localStorage.getItem("selected_collection_slot")})
            </p>
            <button
              className="bg-pk-blue-5 text-pk-white px-2 xs:px-4 py-1 rounded-md z-30 ml-auto shadow-cartoon-sm"
              onClick={() => setIntent(undefined)}
            >
              <span className="text-xs xs:text-sm">Cancel</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col px-2 gap-2 flex-1 min-h-0 overflow-hidden">
        {/* Toggle Header */}
        <StandardFrame variant="purple" className="">
          <div className="grid grid-cols-2 items-center justify-between">
            <div
              className="flex justify-center items-center gap-2"
              style={{
                opacity: activeTab === "inventory" ? 1 : 0.5,
                backgroundColor:
                  activeTab === "inventory"
                    ? "rgba(255, 255, 255, 0.51)"
                    : "transparent",
              }}
            >
              <button
                onClick={() => setActiveTab("inventory")}
                className={cn(
                  "text-lg font-black uppercase tracking-wider drop-shadow-md transition-all",
                  activeTab === "inventory"
                    ? "text-white"
                    : "text-white/40 hover:text-white/60",
                )}
              >
                Inventory
              </button>
            </div>
            <div
              className="flex justify-center items-center gap-2"
              style={{
                opacity: activeTab === "collections" ? 1 : 0.5,
                backgroundColor:
                  activeTab === "collections"
                    ? "rgba(255, 255, 255, 0.51)"
                    : "transparent",
              }}
            >
              <button
                onClick={() => setActiveTab("collections")}
                className={cn(
                  "text-lg font-black uppercase tracking-wider drop-shadow-md transition-all",
                  activeTab === "collections"
                    ? "text-white"
                    : "text-white/40 hover:text-white/60",
                )}
              >
                Collections
              </button>
            </div>
          </div>
        </StandardFrame>

        {/* Content */}
        {activeTab === "inventory" ? (
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <InventoryList
              pokemons={pokemons}
              isLoading={isLoading}
              error={error}
              onRefresh={handleRefresh}
              boxes={boxes}
            />
          </div>
        ) : (
          <CollectionListView />
        )}
      </div>
    </div>
  );
};
